import { createElement } from "react";
import type { Icon } from "@phosphor-icons/react";
import { getCategoryPhosphorIcon } from "@/components/home/variants/categoryPhosphorIcons";
import {
  CATEGORY_AVATAR_DISC,
  CATEGORY_AVATAR_DISC_SELECTED,
  CATEGORY_AVATAR_INK,
} from "@/lib/categories";
import { cn } from "@/lib/utils";

/**
 * 핀 꼬리 길이(px) — 원(알약) 바닥에서 꼬리 끝까지. 10-16 사용자 결정(미리보기 4px).
 *
 * 핀은 전부 **상자 바닥 = 꼬리 끝**으로 그린다. 그래서 지도 마커는 `anchorTop="-100%"`
 * 하나로 꼬리 끝이 정확히 그 좌표를 찍는다. 예전 45° 사각형 꼬리는 상자 밖으로
 * 삐져나와 앵커를 -110% 로 어림해야 했다.
 */
export const MAP_PIN_TAIL = 4;

/** 아이콘 핀 원 지름 — 흰 테두리 4px 포함(주황 원 32px). */
const PIN_D = 40;

/** 꼬리가 원과 만나는 지점의 반폭 */
const TAIL_HALF = 5.5;
/** 꼬리 끝 둥글기 */
const TIP_ROUND = 1.1;

const r2 = (n: number) => Math.round(n * 100) / 100;

/**
 * 원 + 꼬리를 **경로 하나**로. 원 세 겹(흰 알약·흰 테두리·주황 원)과 돌린 사각형을
 * 따로 그리면 배율이 소수인 안드로이드(2.625배 등)에서 가장자리가 제각각 반올림돼
 * 테두리 두께가 들쭉날쭉하고 꼬리가 떠 보였다(10-16).
 */
function pinOutlinePath(d: number, tail: number): string {
  const r = d / 2;
  const joinY = r + Math.sqrt(r * r - TAIL_HALF * TAIL_HALF);
  const tip = d + tail;
  return (
    `M${r2(r - TAIL_HALF)},${r2(joinY)} ` +
    `A${r},${r} 0 1 1 ${r2(r + TAIL_HALF)},${r2(joinY)} ` +
    `L${r2(r + TIP_ROUND)},${r2(tip - TIP_ROUND * 1.2)} ` +
    `Q${r},${tip} ${r2(r - TIP_ROUND)},${r2(tip - TIP_ROUND * 1.2)} Z`
  );
}

const PIN_OUTLINE = pinOutlinePath(PIN_D, MAP_PIN_TAIL);

interface CategoryIconMapPinProps {
  categoryCode?: string;
  icon?: Icon;
  selected?: boolean;
  preciseIcon?: boolean;
  className?: string;
  appearance?: "category" | "venue";
}

/** Icon-only map pin shared with the search-page station markers. */
export function CategoryIconMapPin({
  categoryCode,
  icon,
  selected = false,
  preciseIcon = false,
  className,
  appearance = "category",
}: CategoryIconMapPinProps) {
  const iconCode = categoryCode
    ? preciseIcon
      ? categoryCode
      : categoryCode.split("-").slice(0, 2).join("-")
    : "";

  const MarkerIcon = icon ?? getCategoryPhosphorIcon(iconCode);
  const isVenue = appearance === "venue";
  const height = PIN_D + MAP_PIN_TAIL;
  const c = PIN_D / 2;
  const iconSize = 18;

  // 선택은 알약이 아니라 **원**을 검정으로 — 진한 주황 원 사이에서 색 하나로
  // 갈리고, 흰 테두리는 그대로라 바탕 위 경계가 유지된다(10-02 미리보기 확정).
  const shellFill = isVenue ? "#f97316" : "#ffffff";
  const discFill = isVenue
    ? "#ffffff"
    : selected
      ? CATEGORY_AVATAR_DISC_SELECTED
      : CATEGORY_AVATAR_DISC;

  return (
    <span
      className={cn(
        "relative inline-flex origin-bottom drop-shadow-[0_2px_6px_rgba(0,0,0,0.30)] transition-transform duration-200 hover:scale-105",
        className,
      )}
    >
      <svg
        width={PIN_D}
        height={height}
        viewBox={`0 0 ${PIN_D} ${height}`}
        className="block overflow-visible"
        aria-hidden="true"
      >
        <path d={PIN_OUTLINE} fill={shellFill} />
        <circle cx={c} cy={c} r={c - 4} fill={discFill} />
        {createElement(MarkerIcon, {
          size: iconSize,
          x: c - iconSize / 2,
          y: c - iconSize / 2,
          weight: "regular",
          color: isVenue ? "#f97316" : CATEGORY_AVATAR_INK,
        })}
      </svg>
    </span>
  );
}

/**
 * 알약 핀(묶음 "N곳" 등) 안의 카테고리 원 — 32px, 흰 테두리 2px. 테두리를 CSS border +
 * overflow-hidden 으로 두르지 않고 원 두 개를 한 SVG 에 그린다(위와 같은 이유).
 * 겹쳐 놓을 때(-space-x-3) 흰 테두리가 앞 원과의 경계가 된다.
 */
export function MapPinDisc({
  icon,
  selected = false,
  size = 32,
}: {
  icon: Icon;
  selected?: boolean;
  size?: number;
}) {
  const c = size / 2;
  const iconSize = size === 32 ? 16 : 14;
  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      className="relative z-[1] block shrink-0"
      aria-hidden="true"
    >
      <circle cx={c} cy={c} r={c} fill="#ffffff" />
      <circle
        cx={c}
        cy={c}
        r={c - 2}
        fill={selected ? CATEGORY_AVATAR_DISC_SELECTED : CATEGORY_AVATAR_DISC}
      />
      {createElement(icon, {
        size: iconSize,
        x: c - iconSize / 2,
        y: c - iconSize / 2,
        weight: "regular",
        color: CATEGORY_AVATAR_INK,
      })}
    </svg>
  );
}

/**
 * 알약 핀 꼬리 길이(px). 원형 핀(4px)보다 1px 길다 — 알약은 바닥이 평평해 같은 4px 이
 * 납작한 혹처럼 보였다(10-16 미리보기 B안 5px).
 */
export const MAP_PILL_TAIL = 5;

/**
 * 알약 핀 꼬리 — 알약 바로 아래에 붙여 그리며(1px 겹침), 끝이 상자 바닥이다.
 * `className` 의 `text-*` 로 알약과 같은 색을 준다(currentColor).
 *
 * 핀에는 회색 경계선을 두르지 않는다 — 알약의 1px 경계(ring)가 꼬리 위를 가로질러
 * 꼬리가 알약에 묻혀 보였다(10-16). 바탕과의 구분은 그림자가 맡는다.
 */
export function MapPinTail({ className }: { className?: string }) {
  const w = 12;
  const h = MAP_PILL_TAIL + 1;
  const m = w / 2;
  const tipY = r2(h - 1.2);
  return (
    <svg
      width={w}
      height={h}
      viewBox={`0 0 ${w} ${h}`}
      className={cn("-mt-px block", className)}
      aria-hidden="true"
    >
      <path
        d={`M0,0 L${w},0 L${m + 1},${tipY} Q${m},${h} ${m - 1},${tipY} Z`}
        fill="currentColor"
      />
    </svg>
  );
}
